import {useEffect, useRef, useState} from 'react';

import {FIRST_ITEM_INDEX_INITIAL} from '../constants';
import {LogLineData} from '../LogLineRenderer';

export function useFirstItemIndex(
  lines: readonly LogLineData[],
  isLoadingPrevious: boolean
): number {
  // Track 'firstItemIndex' so that 'Virtuoso' can maintain a stable scroll
  // position as older logs are prepended.
  const [firstItemIndex, setFirstItemIndex] = useState(
    FIRST_ITEM_INDEX_INITIAL
  );
  const linesCountPrevious = useRef(lines.length); // Don't re-render on change.
  const wasLoadingPrevious = useRef(false); // Don't re-render on change.

  useEffect(() => {
    // Once next page fetch begins, record the last known count of lines.
    if (!wasLoadingPrevious.current && isLoadingPrevious) {
      linesCountPrevious.current = lines.length;
    }

    // Once next page fetch ends, update the starting logical index based on the
    // number of additional logs prepended. Note that this behavior is critical
    // for maintaining a stable scroll position as older logs are prepended to
    // 'lines'.
    if (wasLoadingPrevious.current && !isLoadingPrevious) {
      const countPrepended = lines.length - linesCountPrevious.current;
      if (countPrepended > 0) {
        setFirstItemIndex(indexPrevious => indexPrevious - countPrepended);
      }
    }

    wasLoadingPrevious.current = isLoadingPrevious ?? false;
  }, [lines.length, isLoadingPrevious]);

  return firstItemIndex;
}
